import React, { useState, useEffect } from 'react';
import Header from '@modules/common/components/Header';
import { ModalHeader } from '@modules/common/components/BaseModal/ModalHeader';
import { ModalContent } from '@modules/common/components/BaseModal/ModalContent';
import { ModalActions } from '@modules/common/components/BaseModal/ModalActions';

import { Moment } from 'moment-timezone';
import PrimaryButton from '@modules/common/components/buttons/PrimaryButton';
import SecondaryButton from '@modules/common/components/buttons/SecondaryButton';

import { OAuthFlow } from '../../../components/types/oauth-client/index';
import { SUPPORTED_OAUTH_FLOWS } from '../const';
import ClientForm from '../ClientForm';
import { EMPTY_CLIENT_NAME, validateClientForm } from '../utils';

type Props = {
  handleClose: () => void;
  createClient: (
    selectedOAuthFlow: OAuthFlow,
    clientName: string,
    allowedOriginDomains: string[] | undefined,
    isSPAEnabled: boolean,
    expirationDisabled: boolean,
    expiryDate?: Moment,
  ) => void;
  creatingClient: boolean;
};

function NewClientForm({ handleClose, createClient, creatingClient }: Props) {
  const [selectedOAuthFlow, setSelectedOAuthFlow] = useState<OAuthFlow>(
    SUPPORTED_OAUTH_FLOWS[0].value,
  );
  const [clientName, setClientName] = useState<string>(EMPTY_CLIENT_NAME);
  const [expiryDate, setExpiryDate] = useState<Moment | undefined>();
  const [expirationDisabled, setExpirationDisabled] = useState<boolean>(false);
  const [hasAcceptedExpiryRisk, setHasAcceptedExpiryRisk] = useState<boolean>(false);
  const [isSetupForSPA, setIsSetupForSPA] = useState<boolean>(false);
  const [allowedOriginDomains, setAllowedOriginDomains] = useState<string[]>();
  const [hasAcceptedSPARisk, setHasAcceptedSPARisk] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  useEffect(() => {
    setIsFormValid(
      validateClientForm(
        clientName,
        isSetupForSPA,
        hasAcceptedSPARisk,
        expirationDisabled,
        hasAcceptedExpiryRisk,
      ),
    );
  }, [clientName, isSetupForSPA, hasAcceptedSPARisk, expirationDisabled, hasAcceptedExpiryRisk]);

  function onSubmitForm() {
    if (isFormValid) {
      createClient(
        selectedOAuthFlow!,
        clientName,
        allowedOriginDomains,
        hasAcceptedSPARisk,
        expirationDisabled,
        expiryDate,
      );
    }
  }

  return (
    <>
      <ModalHeader>
        <Header content="Create a new OAuth client" icon="add" />
      </ModalHeader>
      <ModalContent>
        <ClientForm
          isLoading={creatingClient}
          selectedOAuthFlow={selectedOAuthFlow}
          setSelectedOAuthFlow={setSelectedOAuthFlow}
          clientName={clientName}
          setClientName={setClientName}
          expiryDate={expiryDate}
          setExpiryDate={setExpiryDate}
          expirationDisabled={expirationDisabled}
          setExpirationDisabled={setExpirationDisabled}
          isSetupForSPA={isSetupForSPA}
          setIsSetupForSPA={setIsSetupForSPA}
          allowedOriginDomains={allowedOriginDomains}
          setAllowedOriginDomains={setAllowedOriginDomains}
          hasAcceptedSPARisk={hasAcceptedSPARisk}
          setHasAcceptedSPARisk={setHasAcceptedSPARisk}
          hasAcceptedExpiryRisk={hasAcceptedExpiryRisk}
          setHasAcceptedExpiryRisk={setHasAcceptedExpiryRisk}
        />
      </ModalContent>
      <ModalActions>
        <SecondaryButton icon="remove" onClick={handleClose} content="Close" />
        <PrimaryButton
          content="Create"
          onClick={onSubmitForm}
          loading={creatingClient}
          disabled={!isFormValid}
          icon="add"
        />
      </ModalActions>
    </>
  );
}

export default NewClientForm;
